/* istanbul ignore file */

import axios, { CancelTokenSource } from "axios";
import { objectToCamel, objectToSnake } from "ts-case-convert/no-split-numbers";
import subdomain from "lib/subdomain";
import TokenService from "./token.service";
import _ from "lodash";
import { PickupContact } from "types/pickupContact";

export const HOSTNAME = process.env.REACT_APP_API_BASE_URL || "/";

export const api = axios.create({
  baseURL: `${HOSTNAME}api/v1`,
  withCredentials: true,
});

const requestCancelSourcesByKey: { [cancelKey: string]: CancelTokenSource } = {};

export const cancelRequest = (cancelKey: string) => {
  const cancelSource = requestCancelSourcesByKey[cancelKey];
  if (cancelSource) {
    cancelSource.cancel();
    delete requestCancelSourcesByKey[cancelKey];
  }
};

export const emptyStringsToNull = (data: PickupContact[]) => {
  return data.map((contact: PickupContact) => {
    return _.mapValues(contact, (value) => (value === "" ? null : value));
  });
};

export const emptyStringToNull = (data: any) => {
  return _.mapValues(data, (value) => (value === "" ? null : value));
};

export const documentsAuthCredentials = {
  withCredentials: true,
  headers: {
    "access-token": TokenService.get("accessToken") || "",
    client: TokenService.get("client") || "",
    uid: TokenService.get("uid") || "",
    expiry: TokenService.get("expiry") || "",
    "token-type": "Bearer",
  },
};

api.interceptors.request.use(
  (config: any) => {
    if (config.cancelKey) {
      cancelRequest(config.cancelKey);
      const newCancelSource = axios.CancelToken.source();
      requestCancelSourcesByKey[config.cancelKey] = newCancelSource;
      config.cancelToken = newCancelSource.token;
    }
    if (config.data) {
      config.data = objectToSnake(config.data);
      if (config.data.location) {
        if (config.data.location.location_contacts) {
          config.data.location.location_contacts = emptyStringsToNull(config.data.location.location_contacts);
        }
      }
      if (config.data.asset_retrieval) {
        config.data.asset_retrieval = emptyStringToNull(config.data.asset_retrieval);
      }
    }
    if (TokenService.get("accessToken")) config.headers["access-token"] = TokenService.get("accessToken");
    if (TokenService.get("client")) config.headers["client"] = TokenService.get("client");
    if (TokenService.get("uid")) config.headers["uid"] = TokenService.get("uid");
    if (TokenService.get("expiry")) config.headers["expiry"] = TokenService.get("expiry");
    config.headers["token-type"] = "Bearer";

    config.headers.Accept = "application/json";

    const organizationSubdomain = subdomain();
    if (!!localStorage.getItem("sandbox")) config.headers["mode"] = "sandbox";
    if (organizationSubdomain) {
      config.headers["organization-subdomain"] = organizationSubdomain;
    }
    return config;
  },
  (error: Error) => {
    return Promise.reject(error);
  },
);

api.interceptors.response.use(
  function (response) {
    if (response.config?.responseType === "blob" || response.config?.responseType === "arraybuffer") return response;

    return { ...response, data: objectToCamel(response.data) };
  },
  function (error) {
    if (error?.code === "ERR_NETWORK") {
      TokenService.remove("accessToken");
      if (!window.location.href.includes("no-backend-connection")) window.location.href = "/no-backend-connection";
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !window.location.href.includes("login")) {
      TokenService.remove("accessToken");
      window.location.href = "/login";
    }

    return Promise.reject(error);
  },
);

export const fetcher = (url: string) => api.get(url).then((res) => res.data);
